import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpResponseBase } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay } from 'rxjs';
import { environment } from '../../environments/environment';
import { PendingChangesService } from './pending-changes.service';

export type MaintenanceMode = 'maintenance' | 'outage';

export const MAINTENANCE_ROUTE = '/maintenance';

// DOVE-1245 — routes the app to /maintenance when the API is down for planned
// maintenance (503) or unreachable (other 5xx / network error confirmed by a health probe).
@Injectable({
	providedIn: 'root'
})
export class MaintenanceService {
	mode: MaintenanceMode | null = null;
	eta: Date | null = null;
	returnUrl = '/';

	readonly apiOrigin = environment.apiUrl.replace(/\/api\/v1\/?$/, '');
	readonly healthUrl = `${this.apiOrigin}/api/health`;

	// Built on HttpBackend so health probes skip every interceptor — a failing probe can never re-trigger itself.
	private readonly http: HttpClient;
	private probe$: Observable<HttpResponseBase> | null = null;

	constructor(
		httpBackend: HttpBackend,
		private router: Router,
		private pendingChanges: PendingChangesService
	) {
		this.http = new HttpClient(httpBackend);
	}

	isApiRequest(url: string): boolean {
		if (url.startsWith(this.healthUrl)) {
			return false;
		}

		return this.apiOrigin ? url.startsWith(this.apiOrigin) : url.startsWith('/');
	}

	handleError(error: HttpErrorResponse): void {
		if (this.isOnMaintenancePage()) {
			return;
		}

		if (error.status === 503) {
			this.enter('maintenance', error);
			return;
		}

		if (error.status === 0 || error.status >= 500) {
			this.probe().subscribe((response) => {
				if (response.status === 200 || this.isOnMaintenancePage()) {
					return;
				}

				this.applyProbeResult(response);
			});
		}
	}

	probe(): Observable<HttpResponseBase> {
		if (!this.probe$) {
			this.probe$ = this.http.get(this.healthUrl, { observe: 'response' }).pipe(
				catchError((error: HttpErrorResponse) => of(error)),
				finalize(() => (this.probe$ = null)),
				shareReplay(1)
			);
		}

		return this.probe$;
	}

	applyProbeResult(response: HttpResponseBase): void {
		this.enter(response.status === 503 ? 'maintenance' : 'outage', response);
	}

	recover(): void {
		const target = this.returnUrl || '/';

		this.mode = null;
		this.eta = null;
		this.returnUrl = '/';

		this.router.navigateByUrl(target);
	}

	private enter(mode: MaintenanceMode, response: HttpResponseBase): void {
		this.mode = mode;
		this.eta = mode === 'maintenance' ? this.parseRetryAfter(response.headers?.get('Retry-After')) : null;

		if (this.isOnMaintenancePage()) {
			return;
		}

		this.returnUrl = window.location.pathname + window.location.search || '/';

		// Same reason as AuthInterceptor's 403 handling: PrimeNG dialog masks outlive route changes.
		document.querySelectorAll('.p-dialog-mask').forEach((el) => el.remove());

		this.pendingChanges.discardAll();
		this.router.navigateByUrl(MAINTENANCE_ROUTE);
	}

	private parseRetryAfter(value: string | null | undefined): Date | null {
		if (!value?.trim()) {
			return null;
		}

		// Retry-After is either delay-seconds or an HTTP-date (`php artisan down --retry="<date>"`).
		const time = /^\d+$/.test(value.trim()) ? Date.now() + Number(value) * 1000 : Date.parse(value);

		return Number.isNaN(time) || time <= Date.now() ? null : new Date(time);
	}

	private isOnMaintenancePage(): boolean {
		return window.location.pathname.startsWith(MAINTENANCE_ROUTE);
	}
}
